import type { AdType } from '@/modules/adv/types'
import type { GameAttributes, GameEvent, User } from '@/types'
import type { FetchResult } from '@/utils/requests/types'

import { MessageAction, OutcomingMessage } from '../main-frame/message-types'

export interface Order {
    amount: number
    gameHru: string
    gameTitle: string
    type: 'order'
    attributes: GameAttributes
    description?: string
    subtitle?: string
    photoUrl?: string
    externalId?: string
    bridgeId?: string
    placement?: AdType
}

export type DisableAdOrder = {
    amount: number
    type: 'disable-ad-order'
    durationInDays: 1 | 7 | 30
    externalId: string
    attributes: GameAttributes
    placement?: AdType
    isTopUp?: boolean
    subscription: 'basic-days'
}

export interface Topup {
    amount?: number
    type: 'topup'
}

interface Purchase {
    id: string
    purchaseToken: string
    status: string
    externalId: string
    bridgeId?: string
    [key: string]: unknown
}

export type PurchaseStatus = PaidStatus | FailedStatus

interface PaidStatus {
    status: 'PAID'
    orderId: string
    amount: number
    externalId?: string
}

interface FailedStatus {
    status: 'FAILED'
    error: string
}

export type ChargeResponse<T extends Order | DisableAdOrder | Topup> = T extends Order | DisableAdOrder
    ? Awaited<FetchResult<PurchaseStatus>>
    : User | null

export type PaymentModalOrderResponse<T = Order | DisableAdOrder | Topup | null> = T extends
    | Order
    | DisableAdOrder
    | Topup
    ? Promise<ChargeResponse<T>>
    : undefined

export type ChargeUserFunction = <T extends Order | DisableAdOrder | Topup | null>(
    value: T,
) => PaymentModalOrderResponse<T>

interface IgpParams {
    gameId: string
    orderId: string
    externalId: string
}

type ConfirmDeliveryFn = (params: IgpParams) => Promise<void>
type GetPurchasesFn = (gameId: string) => Promise<Purchase[]>
type ConsumePurchaseFn = (params: IgpParams) => Promise<void>

interface CreateInGamePaymentsOptions {
    gameId: string
    gameHru: string
    gameTitle: string
    chargeUser: ChargeUserFunction
    attributes: GameAttributes
    confirmDelivery?: ConfirmDeliveryFn
    getPurchases?: GetPurchasesFn
    consumePurchase?: ConsumePurchaseFn
}

const purchaseMessage = (res: Awaited<FetchResult<PurchaseStatus>>, event: GameEvent) => {
    let payload = res.data
    if (res.errors.length) {
        payload = {
            status: 'FAILED',
            error: res.errors.join(', '),
        }
    }

    return {
        type: 'in-game-payments',
        action: MessageAction.purchaseStatus,
        originalMessageEvent: event,
        payload,
    } as OutcomingMessage
}

export const createInGamePayments = ({
    gameId,
    gameHru,
    gameTitle,
    chargeUser,
    attributes,
    confirmDelivery,
    getPurchases,
    consumePurchase,
}: CreateInGamePaymentsOptions) => {
    const messageHandler = async (event: GameEvent) => {
        switch (event.data.action) {
            case MessageAction.requestPurchase: {
                try {
                    const purchaseData = await chargeUser<Order>({
                        type: 'order',
                        gameHru,
                        gameTitle,
                        attributes,
                        ...event.data.payload,
                    } as Order)
                    return purchaseMessage(purchaseData, event)
                } catch (e) {
                    console.error('Caught error during purchase request:', e)
                    return purchaseMessage(
                        {
                            errors: [e?.toString() as string],
                            data: null,
                            // @ts-expect-error fill later
                            originalResponse: null,
                        },
                        event,
                    )
                }
            }
            case MessageAction.requestTopup: {
                try {
                    const userInfo = await chargeUser({ type: 'topup', ...event.data.payload } as Topup)
                    return {
                        type: 'in-game-payments',
                        action: MessageAction.topupStatus,
                        originalMessageEvent: event as GameEvent,
                        payload: userInfo,
                    } as OutcomingMessage
                } catch (e) {
                    return purchaseMessage(
                        {
                            errors: [e?.toString() as string],
                            data: null,
                            // @ts-expect-error fill later
                            originalResponse: null,
                        },
                        event,
                    )
                }
            }
            case MessageAction.confirmDelivery: {
                if (confirmDelivery) {
                    try {
                        const { orderId, externalId } = (event.data.payload ?? {}) as {
                            orderId: string
                            externalId: string
                        }
                        await confirmDelivery({ gameId, orderId, externalId })
                    } catch (e) {
                        console.error('Caught error during confirmDelivery:', e)
                    }
                }
                return {
                    type: 'in-game-payments',
                    action: MessageAction.confirmDeliveryStatus,
                    originalMessageEvent: event,
                    payload: {},
                } as OutcomingMessage
            }
            case MessageAction.getPurchases: {
                try {
                    const purchases = getPurchases ? await getPurchases(gameId) : []
                    return {
                        type: 'in-game-payments',
                        action: MessageAction.purchasesResponse,
                        originalMessageEvent: event,
                        payload: { purchases },
                    } as OutcomingMessage
                } catch (e) {
                    console.error('Caught error during getPurchases:', e)
                    return {
                        type: 'in-game-payments',
                        action: MessageAction.purchasesResponse,
                        originalMessageEvent: event,
                        payload: { purchases: [] },
                    } as OutcomingMessage
                }
            }
            case MessageAction.consumePurchase: {
                try {
                    if (consumePurchase) {
                        const { orderId, externalId } = (event.data.payload ?? {}) as {
                            orderId: string
                            externalId: string
                        }
                        await consumePurchase({ gameId, orderId, externalId })
                    }
                    return {
                        type: 'in-game-payments',
                        action: MessageAction.consumePurchaseStatus,
                        originalMessageEvent: event,
                        payload: {},
                    } as OutcomingMessage
                } catch (e) {
                    console.error('Caught error during consumePurchase:', e)
                    return {
                        action: MessageAction.consumePurchaseStatus,
                        originalMessageEvent: event,
                        payload: { error: e?.toString() },
                        type: 'in-game-payments',
                    } as OutcomingMessage
                }
            }
            default:
                return {
                    action: MessageAction.unknownMessageType,
                    originalMessageEvent: event as GameEvent,
                    payload: {
                        error: `Unknown message action "${event.data.action}"`,
                    },
                    type: 'error',
                } as OutcomingMessage
        }
    }

    return messageHandler
}

export type InGamePaymentsMessageHandler = (event: GameEvent) => Promise<OutcomingMessage>
